import { track } from '@amplitude/analytics-browser'
import { cn } from '@dialogueconsulting/sked-ui/lib/utils'
import isEmpty from 'lodash.isempty'
import uniqBy from 'lodash.uniqby'
import { useEffect, useState } from 'react'
import { useScrollDirection } from 'react-use-scroll-direction'

import { fetchPostsPreview, fetchGridPreviewByAccount } from 'api/demoAware'
import type {
  AccountSelectorProps,
  UpcomingPostCardProps,
  ConversationProps,
  PostActionProps,
} from 'Components'
import { Navbar, LoadingPlaceholder, EmptyUpcomingPosts } from 'Components'
import Dropdown from 'Components/Common/Dropdown'
import PoweredBySked from 'Components/Common/PoweredBySked'
import { getLastStatusChangeMessage } from 'Components/conversation/utils'
import MobilePreviewGrid from 'Components/Mobile/MobilePreviewGrid'
import MobileUpcomingPostCard from 'Components/Mobile/MobileUpcomingPostCard'
import { ApprovalsIcon, GridPreviewIcon } from 'Icons'
import {
  datePlaceholderForDraftPost,
  normalizeGridPreviewAllPosts,
  normalizeSkedPreviewPost,
} from 'normalizer'
import { ToggleGroup, ToggleGroupItem } from 'ui/toggle-group'
import { reportError } from 'utils/reportError'
import getInstagramAccountOption from 'utils/getInstagramAccountOption'
import type { PreviewVariant } from 'utils/postPreviewVariant'

interface AppMobileProps {
  isLoading: boolean
  setting: {
    logoUrl: string
    companyName: string
  }
  accountSelectorProps: AccountSelectorProps
  upcomingPosts: {
    id: string
    date: string
    isVisible: boolean
    posts: (UpcomingPostCardProps & {
      isVisible?: boolean
      postIndex: number
    })[]
  }[]
  updatePostState: (
    reviewKey: string,
    postId: string,
    finalState: string
  ) => void
  accountsRaw: any
  conversationProps: ConversationProps
  postActionProps: PostActionProps
  reviewKey: string
  postId: string
  fetchComment?: (reviewKey: string, postId: string) => void
  isReadOnly: boolean
  previewVariant: PreviewVariant
}

type PostOrGridView = 'postView' | 'gridView'

const AppMobile = (props: AppMobileProps) => {
  const {
    isLoading,
    setting: { logoUrl, companyName },
    accountSelectorProps,
    upcomingPosts,
    accountsRaw, // to build skedPreviewPosts
    conversationProps,
    postActionProps,
    reviewKey,
    isReadOnly,
    previewVariant,
    updatePostState,
  } = props

  const [postOrGridView, setPostOrGridView] =
    useState<PostOrGridView>('postView')

  const [skedPreviewsPosts, setSkedPreviewsPosts] = useState<any[]>([])

  const [gridData, setGridData] = useState([])
  const [gridDataCached, setGridDataCached] = useState([])

  const [isSkedPreviewLoading, setIsSkedPreviewLoading] = useState(false)
  const [isGridPreviewLoading, setIsGridPreviewLoading] = useState(false)

  const handlePostGridTabClick = (v: PostOrGridView) => () => {
    if (isGridPreviewLoading) {
      alert('please wait...')
    } else {
      setPostOrGridView(v)
    }

    track('ext-planner-tab-click')
  }

  const [showStickyTab, setShowStickyTab] = useState(true)
  const { isScrollingUp, isScrollingDown } = useScrollDirection()
  useEffect(() => {
    if (window.scrollY >= 130) {
      if (isScrollingUp) {
        setShowStickyTab(true)
      }
      if (isScrollingDown) {
        setShowStickyTab(false)
      }
    } else {
      setShowStickyTab(true)
    }
  }, [isScrollingUp, isScrollingDown])

  useEffect(() => {
    const fetchPostsPreviewAsync = async () => {
      setIsSkedPreviewLoading(true)
      try {
        const skedPreviewPostsResponse = await fetchPostsPreview(reviewKey)

        if (!skedPreviewPostsResponse) {
          return
        }

        const skedPreviewsPosts = skedPreviewPostsResponse.map(
          (skedPreviewResponse: any) =>
            normalizeSkedPreviewPost(skedPreviewResponse, accountsRaw)
        )

        setSkedPreviewsPosts(skedPreviewsPosts)
      } catch (error) {
        reportError(error)
      } finally {
        setIsSkedPreviewLoading(false)
      }
    }

    if (!isLoading) {
      fetchPostsPreviewAsync()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading])

  const accountDropdownOption = getInstagramAccountOption(
    accountSelectorProps.accounts
  )

  const accountIds = accountDropdownOption.map((acc) => acc.id)

  useEffect(() => {
    if (
      postOrGridView === 'gridView' &&
      isEmpty(gridDataCached) &&
      accountIds.length > 0
    ) {
      const fetchData = async () => {
        setIsGridPreviewLoading(true)
        try {
          if (!accountIds || accountIds.length === 0) return

          const posts = await Promise.all(
            accountIds.map((id) => fetchGridPreviewByAccount(reviewKey, id))
          )

          if (!posts || posts.length === 0) {
            return
          }

          const gridPosts = uniqBy(
            posts.filter((p) => p && p.length).flat(1),
            'id'
          )

          if (gridPosts.length > 0) {
            setGridData(
              normalizeGridPreviewAllPosts(gridPosts).filter((post: any) =>
                post.accounts.some((id: string) => accountIds[0] === id)
              )
            )
            setGridDataCached(normalizeGridPreviewAllPosts(gridPosts))
          }
        } catch (error) {
          reportError(error)
        } finally {
          setIsGridPreviewLoading(false)
        }
      }
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postOrGridView, gridDataCached, accountIds])

  return (
    <div className="m-[10px]">
      <Navbar
        logo={logoUrl}
        name={companyName}
        className="-m-[10px] h-[65px] px-[10px] max-[1280px]:px-[10px] [&_img]:mr-[14px] [&_img]:w-8"
      />
      <h4 className="mx-auto mb-4 mt-[26px] text-center font-gotham text-xl font-extrabold text-neutral-800">
        Upcoming Posts
      </h4>
      <div
        className={cn(
          'sticky top-0 z-[3] -mx-[10px] mb-5 flex justify-center bg-white p-[10px] transition-opacity duration-100 ease-in',
          showStickyTab ? 'visible opacity-100' : 'invisible opacity-0'
        )}
        data-testid="postGridPreviewTab"
      >
        <ToggleGroup
          type="single"
          appearance="nav"
          value={postOrGridView}
          aria-label="Post or grid view"
          onValueChange={(value) => {
            if (value) handlePostGridTabClick(value as PostOrGridView)()
          }}
        >
          <ToggleGroupItem value="postView">
            <ApprovalsIcon aria-hidden="true" />
            Post Preview
          </ToggleGroupItem>
          <ToggleGroupItem value="gridView" disabled={isLoading}>
            <GridPreviewIcon aria-hidden="true" />
            Grid Preview
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {postOrGridView === 'postView' && isReadOnly && (
        <div
          data-testid="calendar-view-notification"
          className="mb-[-14px] rounded-[5px] border border-[#4f31ff] bg-[#f6f5ff] px-[14px] py-3 text-base font-bold"
        >
          For a better Calendar view, please view this link on a desktop device.
        </div>
      )}

      {!isLoading && !isGridPreviewLoading && postOrGridView === 'gridView' && (
        <>
          <p className="mx-auto my-[18px] flex justify-center text-sm font-medium">
            Available for Instagram only
          </p>
          <Dropdown
            name="accounts"
            placeholder="Select from options"
            onChange={(selected) => {
              const selectedId = selected.value

              const nextGridData = gridDataCached.filter((post: any) =>
                post.accounts.some((id: string) => selectedId === id)
              )

              if (nextGridData.length > 0) {
                setGridData(nextGridData)
              } else {
                setGridData([])
              }
            }}
            options={accountDropdownOption}
          />
        </>
      )}
      {isLoading ? (
        <LoadingPlaceholder isMobile />
      ) : !isLoading &&
        !isSkedPreviewLoading &&
        isEmpty(upcomingPosts.flatMap((p) => p.posts)) ? (
        <div
          style={{
            marginTop: isReadOnly ? '30px' : 'auto',
          }}
        >
          <EmptyUpcomingPosts />
        </div>
      ) : (
        postOrGridView === 'postView' && (
          <>
            <div>
              {upcomingPosts.map(
                ({ date, posts, id: postRowId, isVisible }) => {
                  const renderPendingCount = (
                    posts: UpcomingPostCardProps[]
                  ) => {
                    let pendingCount = 0
                    posts.forEach((post) => {
                      if (post.status === 'pending') {
                        pendingCount += 1
                      }
                    })
                    return pendingCount
                  }
                  return (
                    <div key={postRowId + date}>
                      <div
                        className={cn(
                          'mb-[18px] ml-3 mt-7 text-base font-bold text-[#454545]',
                          !isVisible && 'hidden'
                        )}
                      >
                        {date && date === datePlaceholderForDraftPost
                          ? `Drafts`
                          : date}
                        <span
                          className={cn(
                            'text-blue-500',
                            date === datePlaceholderForDraftPost
                              ? 'ml-0'
                              : 'ml-2'
                          )}
                        >
                          {renderPendingCount(posts) > 0 && !isReadOnly && (
                            <>
                              <span className="text-[#454545]">
                                {date !== datePlaceholderForDraftPost &&
                                  '•'}{' '}
                              </span>
                              {renderPendingCount(posts)} pending
                            </>
                          )}
                        </span>
                      </div>
                      {posts.map((post) => {
                        const { id, status, platform, time } = post
                        const postMessagesEntry =
                          conversationProps.messages?.find(
                            (item) => item.postId === id
                          )
                        const postMessages = postMessagesEntry?.messages || []
                        const lastStatusChangeMessage =
                          getLastStatusChangeMessage(postMessages)
                        const lastStatusChangeComment =
                          lastStatusChangeMessage?.comment
                        const hasPostedComment = Boolean(
                          lastStatusChangeMessage?.isMessageOwner &&
                          lastStatusChangeComment &&
                          lastStatusChangeComment.trim().length > 0
                        )
                        const lastMessageId = lastStatusChangeMessage?.id
                        const isPostReplied =
                          hasPostedComment ||
                          conversationProps?.isRepliedPosts?.includes(id)

                        return (
                          <div
                            className={isVisible ? 'block' : 'hidden'}
                            key={status + time + id}
                          >
                            <MobileUpcomingPostCard
                              id={id}
                              status={status}
                              statusLabel={post.statusLabel}
                              platform={platform}
                              time={time}
                              when={post.when}
                              skedPreviewPost={skedPreviewsPosts.find(
                                (post) => post.id === id
                              )}
                              updatePostState={updatePostState}
                              previewVariant={previewVariant}
                              conversation={{
                                isLoading: conversationProps.isLoading,
                                isSubmitting: conversationProps.isSubmitting,
                                messages: conversationProps.messages,
                                canReply: status === 'requestChange',
                                isReplied: isPostReplied,
                                submitReply: conversationProps.submitReply,
                                reviewKey,
                                postId: id,
                                fetchComment: conversationProps.fetchComment,
                                isConversationsEnabled:
                                  conversationProps.isConversationsEnabled,
                                collaborators: conversationProps.collaborators,
                                isReadOnly,
                                lastMessageId,
                                isStatusChangesVisible:
                                  conversationProps.isStatusChangesVisible,
                                isExternalNotesVisible:
                                  conversationProps.isExternalNotesVisible,
                                isPostActivityVisible:
                                  conversationProps.isPostActivityVisible,
                                username: conversationProps.username,
                              }}
                              postAction={{
                                ...postActionProps,
                                postId: id,
                                postStatus: status,
                                isMobile: true,
                                canReply: status === 'requestChange',
                                isReplied: isPostReplied,
                                lastMessageId,
                              }}
                              isReadOnly={isReadOnly}
                            />
                          </div>
                        )
                      })}
                    </div>
                  )
                }
              )}
            </div>

            <PoweredBySked className="my-5 flex place-content-center" />
          </>
        )
      )}
      {postOrGridView === 'gridView' && (
        <div className="mx-auto my-[18px] grid place-content-center">
          {isGridPreviewLoading ? (
            'fetching... please wait...'
          ) : isEmpty(gridData) ? (
            <>
              <br />
              We couldn&apos;t find any upcoming posts
            </>
          ) : (
            <MobilePreviewGrid posts={gridData} isReadOnly={isReadOnly} />
          )}
        </div>
      )}
    </div>
  )
}

export default AppMobile
